import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  FormHelperText,
  Button,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import { numberError, textError } from "../../../utils/validationMessage";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import {
  formatNumberInputWithCommas,
  stripNumberFormatting,
} from "../../../utils/common";
import EastIcon from "@mui/icons-material/East";

const ExcutiveCompensation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form fields for Executive Compensation section
  const [formData, setFormData] = useState({
    highestPaidCompensation: "",
    // highestPaidCurrency: "",
    medianEmployeeCompensation: "",
    percentageIncreaseTopExec: "",
    medianPercentageIncreaseEmployees: "",
  });

  const [errors, setErrors] = useState({
    highestPaidCompensation: "",
    // highestPaidCurrency: "",
    medianEmployeeCompensation: "",
    percentageIncreaseTopExec: "",
    medianPercentageIncreaseEmployees: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/governance/getGovernance`, {
          params: {
            id: id, // Pass the id to get the specific data
          },
        });

        // If data exists, populate the form with the fetched values
        const fetchedData = response.data.data[0]; // Assuming the API returns an array of governance data

        if (fetchedData) {
          const exec = fetchedData.executiveCompensation || {};
          setFormData({
            highestPaidCompensation:
              exec.highestPaidCompensation !== undefined &&
              exec.highestPaidCompensation !== null &&
              exec.highestPaidCompensation !== ""
                ? formatNumberInputWithCommas(
                    String(exec.highestPaidCompensation),
                  )
                : "",
            // highestPaidCurrency: exec.highestPaidCurrency || "",
            medianEmployeeCompensation:
              exec.medianEmployeeCompensation !== undefined &&
              exec.medianEmployeeCompensation !== null &&
              exec.medianEmployeeCompensation !== ""
                ? formatNumberInputWithCommas(
                    String(exec.medianEmployeeCompensation),
                  )
                : "",
            percentageIncreaseTopExec:
              exec.percentageIncreaseTopExec !== undefined &&
              exec.percentageIncreaseTopExec !== null &&
              exec.percentageIncreaseTopExec !== ""
                ? formatNumberInputWithCommas(
                    String(exec.percentageIncreaseTopExec),
                  )
                : "",
            medianPercentageIncreaseEmployees:
              exec.medianPercentageIncreaseEmployees !== undefined &&
              exec.medianPercentageIncreaseEmployees !== null &&
              exec.medianPercentageIncreaseEmployees !== ""
                ? formatNumberInputWithCommas(
                    String(exec.medianPercentageIncreaseEmployees),
                  )
                : "",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Handle input change with validation
  // const handleChange = (field, value) => {
  //   // Update form data
  //   setFormData((prev) => ({
  //     ...prev,
  //     [field]: value,
  //   }));

  //   // Clear error when user starts typing
  //   setErrors((prev) => ({
  //     ...prev,
  //     [field]:
  //       value === ""
  //         ? "Please fill this field."
  //         : /^[0-9]+$/.test(value)
  //         ? ""
  //         : numberError,
  //   }));
  // };

  const isNumeric = (val) =>
    /^\d*\.?\d*$/.test(stripNumberFormatting(val || ""));
  const percentageFields = new Set([
    "percentageIncreaseTopExec",
    "medianPercentageIncreaseEmployees",
  ]);

  const handleChange = (field, value) => {
    const formatted = formatNumberInputWithCommas(value);
    const raw = stripNumberFormatting(formatted);

    if (raw !== "" && !isNumeric(raw)) {
      return;
    }

    // Percentage fields should not exceed 100
    if (
      percentageFields.has(field) &&
      raw !== "" &&
      Number(raw) > 100
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: formatted,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]:
        formatted === ""
          ? textError
          : percentageFields.has(field) && raw !== "" && Number(raw) > 100
          ? "Percentage should not exceed 100%."
          : "",
    }));
  };

  const fields = [
    {
      label: "Highest-paid individual's annual compensation (In Rupees)",
      field: "highestPaidCompensation",
      placeholder: "Enter Number",
      withCurrency: true,
      col: 12,
    },
    {
      label:
        "Median annual total compensation of employees, excluding the highest earner",
      field: "medianEmployeeCompensation",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Percentage increase in top executive compensation",
      field: "percentageIncreaseTopExec",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label:
        "Median percentage increase in annual compensation of employees, excluding the highest earner",
      field: "medianPercentageIncreaseEmployees",
      placeholder: "Enter Number",
      col: 12,
    },
  ];

  const Move = async () => {
    const newErrors = {};
    let hasErrors = false;

    // Validate required fields
    Object.entries(formData).forEach(([key, value]) => {
      const raw = stripNumberFormatting(value);
      if (value === "") {
        newErrors[key] = textError;
        hasErrors = true;
      } else if (
        !isNumeric(raw)
      ) {
        newErrors[key] = numberError;
        hasErrors = true;
      } else if (percentageFields.has(key) && Number(raw) > 100) {
        newErrors[key] = "Percentage should not exceed 100%.";
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await put(`/governance/updateGovernance`, {
        id: id, // company/governance id
        executiveCompensation: {
          highestPaidCompensation: stripNumberFormatting(
            formData.highestPaidCompensation,
          ),
          medianEmployeeCompensation: stripNumberFormatting(
            formData.medianEmployeeCompensation,
          ),
          percentageIncreaseTopExec: stripNumberFormatting(
            formData.percentageIncreaseTopExec,
          ),
          medianPercentageIncreaseEmployees: stripNumberFormatting(
            formData.medianPercentageIncreaseEmployees,
          ),
        },
        stepsCompleted: {
          executiveCompensation: true,
        },
        module: "annual",
      });
      console.log("Data saved successfully:", response.data);
      navigate(`/governance/data/risk-management/${id}`);
    } catch (error) {
      console.error("Error saving data:", error.response || error);
    }
  };


  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Box
        sx={{
          width: "100%",
          margin: "20px auto",
          padding: "20px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start", // Align content to the left
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >

        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{
            fontWeight: "bolder",
            margin: "1.2% 0",
            textDecoration: "underline",
          }}
        >
          A. EXECUTIVE COMPENSATION
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: "24px",
            rowGap: "16px",
            width: "100%",
            maxWidth: "900px",
          }}
        >
          {fields.map((field, index) => (
            <Box
              key={index}
              sx={{
                flex:
                  field.col === 12
                    ? "1 1 100%"
                    : "0 0 calc(50% - 24px)",
                minWidth: field.col === 12 ? "100%" : "360px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {`${index + 1}.`}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", margin:"1.2% 0" }}
                >
                  {field.label}
                </Typography>
              </Box>
              {/* {field.withCurrency ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    ml: "1.2rem",
                  }}
                >
                  <Box sx={{ flex: 2, display: "flex", flexDirection: "column" }}>
                    <TextField
                      value={formData[field.field]}
                      onChange={(e) =>
                        handleChange(field.field, e.target.value)
                      }
                      placeholder={field.placeholder}
                      variant="outlined"
                      fullWidth
                      error={!!errors[field.field]}
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "[0-9.]*",
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 40,
                        },
                      }}
                    />
                    <FormHelperText
                      sx={{
                        color: "#D34040",
                        fontSize: "14px",
                        minHeight: "20px",
                        marginLeft: 0,
                      }}
                    >
                      {errors[field.field] || " "}
                    </FormHelperText>
                  </Box>
                </Box>
              ) : ( */}
               { (<TextField
                  value={formData[field.field]}
                  onChange={(e) => handleChange(field.field, e.target.value)}
                  placeholder={field.placeholder}
                  variant="outlined"
                  
                  error={!!errors[field.field]}
                  inputProps={{
                    inputMode: "decimal",
                    pattern: "[0-9.]*",
                  }}
                  sx={{
                    width: "70%",
                    "& .MuiInputBase-root": {
                      height: 40, // Reduce the text field height
                    },
                    ml: "1.2rem",
                  }}
                />
              )}
              {errors[field.field] && (
                <FormHelperText sx={{ color: "#D34040", fontSize: "14px", marginLeft: "1.2rem" }}>
                  {errors[field.field]}{" "}
                  {/* Customize the error text appearance */}
                </FormHelperText>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: "100%",
            mt: "10%",
            mb: 5,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            sx={{ bgcolor: "#1C486B", textTransform: "none" }}
            onClick={Move}
          >
            Save & Continue <EastIcon sx={{ ml: 2 }} />
          </Button>
        </Box>

      </Box>
    </MainDashboardLayout>
  );
};

export default ExcutiveCompensation;
